import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Store } from 'components/common/types/Store.types';
import { CreativeStatus } from 'components/common/types/Creative.types';
import { cancelFunctions } from 'utils/cancelFunctions';
import {
  approveCreative,
  flagCreative,
  pendingApproveCreative,
  sendCreativeForApproval,
} from 'modules/api/previewContent';
import { addToastNotification } from 'store/notification/reducer';

export type UsePreviewContentButtons = {
  creativeStatus: CreativeStatus;
  isCreativeButtonDisabled: boolean;
  handleSendForApproval: () => Promise<void>;
  handlePendingApprovalCreative: () => Promise<void>;
  handleApproveCreative: () => Promise<void>;
  handleFlagCreative: () => Promise<void>;
};

export const usePreviewContentButtons = ({
  loadCreativesDetails,
  isFetchingCreativeDetails,
}: {
  loadCreativesDetails: () => Promise<void>;
  isFetchingCreativeDetails: boolean;
}): UsePreviewContentButtons => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const moderation = useSelector((state: Store) => state.creative.moderation);
  const isCreativeDeleted = useSelector((state: Store) => state.creative.deleted);
  const marketId = useSelector((state: Store) => state.creative.marketId);

  const creativeStatus = moderation[0]?.status ?? CreativeStatus.PENDING;
  const isCreativeButtonDisabled = !!isCreativeDeleted || isFetchingCreativeDetails;

  const handleSendForApproval = async (): Promise<void> => {
    try {
      await sendCreativeForApproval(cancelFunctions, { marketId });

      dispatch(
        addToastNotification({
          description: 'Creative sent for approval',
          timeout: 2000,
          type: 'Success',
        }),
      );

      await loadCreativesDetails();
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const handlePendingApprovalCreative = async (): Promise<void> => {
    try {
      await pendingApproveCreative(cancelFunctions, { marketId });

      dispatch(
        addToastNotification({
          description: 'Creative Pending approval',
          timeout: 2000,
          type: 'Success',
        }),
      );
      navigate('/content-management/creatives');
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const handleApproveCreative = async (): Promise<void> => {
    try {
      await approveCreative(cancelFunctions, { marketId });

      dispatch(
        addToastNotification({
          description: 'Creative Approved',
          timeout: 2000,
          type: 'Success',
        }),
      );
      navigate('/content-management/creatives');
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const handleFlagCreative = async (): Promise<void> => {
    try {
      await flagCreative(cancelFunctions, { marketId });

      dispatch(
        addToastNotification({
          description: 'Creative Flagged',
          timeout: 2000,
          type: 'Success',
        }),
      );

      await loadCreativesDetails();
      // eslint-disable-next-line no-empty
    } catch {}
  };

  return {
    creativeStatus,
    isCreativeButtonDisabled,
    handleApproveCreative,
    handleFlagCreative,
    handlePendingApprovalCreative,
    handleSendForApproval,
  };
};
